import { Button, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { REPLAY_BLOCK_CLASS } from '@/features/amplitude/privacy'
import { useAnalyticsTrack } from '@/features/amplitude'

import { FUNNEL_PAGE_CLS } from '../lib/constants'

/**
 * Distinct from the report's consult CTA (`Compliance Report CTA Clicked`): same
 * destination, different question. This one measures people who reached pricing
 * and chose to talk to someone instead of buying, which is the whole reason the
 * bar exists.
 */
export const PLANS_CONSULT_CLICK_EVENT = 'compliance plans consult clicked'

interface ConsultCardProps {
  /** Scheduler link, already prefilled for this practitioner. */
  consultUrl: string
}

/**
 * From `lg`, pinned to the bottom of the viewport rather than placed in the
 * page, so the offer to talk to someone stays reachable however far down the
 * plans a reader has scrolled. Its own band of color keeps it off the plans'
 * surface entirely: nothing about it can be compared against a tier, which is
 * the whole risk of putting a second call to action on a pricing page.
 *
 * Below `lg` it sits in the page under the due-today total, because the pay
 * button takes the pinned slot there. `CompliancePlanPricing` reserves the
 * bottom padding for whichever one is pinned.
 */
export const ConsultCard: React.FC<ConsultCardProps> = ({ consultUrl }) => {
  const track = useAnalyticsTrack()

  return (
    // `z-50` must stay scoped to `lg`: a flex item with a z-index other than
    // `auto` makes a stacking context even when static, so unscoped this tied
    // with the pay bar's `max-lg:z-50` and painted over the checkout button.
    <div className="bg-green-200 lg:fixed lg:bottom-0 lg:left-0 lg:right-0 lg:z-50">
      <div
        className={`${FUNNEL_PAGE_CLS} flex flex-wrap items-center justify-between gap-x-6 gap-y-3 py-5`}
      >
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <Text variant={TypographyStyles.DISPLAY_XXS} color={TextColors.WHITE}>
            Not sure yet?
          </Text>
          <Text
            variant={TypographyStyles.BODY_SM}
            className="text-neutral-0/80"
          >
            Book a free 30 minute call with a real person.
          </Text>
        </div>
        <Button asChild variant="secondary" size="sm">
          {/* The href carries the practitioner's name and email, and neither replay
              backend masks attribute values — so this link is excluded from both.
              Blocking hides it from replay entirely, which is why the click is
              tracked explicitly. */}
          <a
            href={consultUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-dd-privacy="hidden"
            className={REPLAY_BLOCK_CLASS}
            onClick={() => track(PLANS_CONSULT_CLICK_EVENT)}
          >
            Book a consult
          </a>
        </Button>
      </div>
    </div>
  )
}
